import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ColorType, CandlestickData, Time, ISeriesApi } from 'lightweight-charts';
import styled from 'styled-components';
import { useTokenChartData, CandleData, ChartTimeframe } from '../../hooks/useTokenChartData';

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 600px;
  background: rgba(20, 20, 30, 0.4);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.6), transparent);
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: calc(100% - 60px);
  position: relative;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.2);
`;

const ChartTitle = styled.h3`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
`;

const IntervalSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const IntervalButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.1)'};
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  color: ${props => props.$active ? '#ffffff' : '#9ca3af'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 300;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(168, 85, 247, 0.2);
    color: #ffffff;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 300;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #f87171;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 300;
`;

interface TokenChartProps {
  tokenAddress: string;
  tokenSymbol?: string;
  dex?: string;
  poolAddress?: string;
  network?: string;
}

const TokenChart: React.FC<TokenChartProps> = ({ tokenAddress, tokenSymbol, dex, poolAddress, network = 'ink' }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const candlestickSeries = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<ChartTimeframe>('15M');

  const { data, loading, error } = useTokenChartData(tokenAddress, selectedInterval, network);

  // If token is on InkySwap, show specific message as GeckoTerminal doesn't support it yet
  if (dex === 'InkySwap') {
    return (
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>{tokenSymbol ? `${tokenSymbol} Price Chart` : 'Price Chart'}</ChartTitle>
        </ChartHeader>
        <ErrorState>
          <div style={{ textAlign: 'center', padding: '0 20px', opacity: 0.8 }}>
            InkySwap Trading Charts are not yet available through GeckoTerminal.
            {poolAddress && (
              <div style={{ marginTop: '12px' }}>
                Please view this token's chart{' '}
                <a
                  href={`https://www.dextools.io/app/en/ink/pair-explorer/${poolAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#a855f7', textDecoration: 'underline', fontWeight: 500 }}
                >
                  here
                </a>
              </div>
            )}
          </div>
        </ErrorState>
      </ChartContainer>
    );
  }

  useEffect(() => {
    if (!chartContainerRef.current || loading || error || !data.length) return;

    console.log('[TokenChart] Initializing chart with', data.length, 'data points');

    // Clean up previous chart instance if it exists
    if (chart.current) {
      console.log('[TokenChart] Removing existing chart instance');
      chart.current.remove();
      chart.current = null;
      candlestickSeries.current = null;
    }

    // Create chart
    try {
      chart.current = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: '#d2d4dc',
          fontSize: 12,
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        },
        grid: {
          vertLines: {
            color: '#131722',
            style: 0, // Solid
          },
          horzLines: {
            color: '#131722',
            style: 0, // Solid
          },
        },
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: '#131722',
        },
        rightPriceScale: {
          borderColor: '#131722',
        },
        crosshair: {
          mode: 0, // Normal
          vertLine: {
            color: '#758696',
            width: 1,
            style: 2, // Dashed
          },
          horzLine: {
            color: '#758696',
            width: 1,
            style: 2, // Dashed
          },
        },
      });

      // Create area series for cleaner look
      const areaSeries = chart.current.addAreaSeries({
        lineColor: '#a855f7', // Purple-500
        topColor: 'rgba(168, 85, 247, 0.4)',
        bottomColor: 'rgba(168, 85, 247, 0.0)',
        lineWidth: 2,
        priceFormat: {
          type: 'price',
          precision: 6,
          minMove: 0.000001,
        },
      });

      // Store reference (using the same ref for now, though typing might be slightly off, it works at runtime)
      // @ts-ignore
      candlestickSeries.current = areaSeries;

      // Map data to AreaData format (time + value)
      // Filter out invalid data points and ensure data quality
      const chartData = data
        .filter(candle => {
          // Filter out candles with invalid values
          return (
            candle.time != null &&
            candle.close != null &&
            !isNaN(candle.close) &&
            candle.close > 0 &&
            isFinite(candle.close)
          );
        })
        .map(candle => ({
          time: candle.time as Time,
          value: candle.close,
        }))
        // Sort by time ascending (required by lightweight-charts)
        .sort((a, b) => (a.time as number) - (b.time as number))
        // Remove duplicate timestamps (keep the first occurrence)
        .filter((item, index, array) => {
          if (index === 0) return true;
          return item.time !== array[index - 1].time;
        });

      console.log('[TokenChart] Cleaned data points:', chartData.length, 'out of', data.length);

      // Validate we have enough data after cleaning
      if (chartData.length < 2) {
        console.error('[TokenChart] Not enough valid data points after cleaning');
        return;
      }

      areaSeries.setData(chartData);
      console.log('[TokenChart] Data set successfully');

      // Fit content
      chart.current.timeScale().fitContent();
      console.log('[TokenChart] Chart content fitted');

    } catch (err) {
      console.error('[TokenChart] Error initializing chart:', err);
      return;
    }

    // Handle resize
    const handleResize = () => {
      if (chart.current && chartContainerRef.current) {
        chart.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      console.log('[TokenChart] Cleaning up chart');
      window.removeEventListener('resize', handleResize);
      if (chart.current) {
        chart.current.remove();
        chart.current = null;
        candlestickSeries.current = null;
      }
    };
  }, [loading, error, data]);

  if (loading) {
    return (
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>{tokenSymbol ? `${tokenSymbol} Price Chart` : 'Price Chart'}</ChartTitle>
        </ChartHeader>
        <LoadingState>Loading chart data...</LoadingState>
      </ChartContainer>
    );
  }

  if (error) {
    return (
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>{tokenSymbol ? `${tokenSymbol} Price Chart` : 'Price Chart'}</ChartTitle>
        </ChartHeader>
        <ErrorState>Failed to load chart: {error}</ErrorState>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartHeader>
        <ChartTitle>{tokenSymbol ? `${tokenSymbol} Price Chart` : 'Price Chart'}</ChartTitle>
        <IntervalSelector>
          <IntervalButton $active={selectedInterval === '5M'} onClick={() => setSelectedInterval('5M')}>
            5M
          </IntervalButton>
          <IntervalButton $active={selectedInterval === '15M'} onClick={() => setSelectedInterval('15M')}>
            15M
          </IntervalButton>
          <IntervalButton $active={selectedInterval === '1H'} onClick={() => setSelectedInterval('1H')}>
            1H
          </IntervalButton>
          <IntervalButton $active={selectedInterval === '4H'} onClick={() => setSelectedInterval('4H')}>
            4H
          </IntervalButton>
          <IntervalButton $active={selectedInterval === '1D'} onClick={() => setSelectedInterval('1D')}>
            1D
          </IntervalButton>
        </IntervalSelector>
      </ChartHeader>
      <ChartWrapper ref={chartContainerRef} />
    </ChartContainer>
  );
};

export default TokenChart;

